import React, { Fragment, useMemo, useState } from "react";
import {
  SideMenuDivider,
  SideMenuList,
  SideMenuListItem,
  SideMenuPanel,
  MultiSectionLayout,
} from "@jugl-web/ui-kit";
import { useTranslations } from "@jugl-web/utils";
import { useNavigationGuardContext } from "@jugl-web/domain-resources/common/providers/NavigationGuardProvider";
import { useUpdateLiveChatAppSettings } from "@jugl-web/domain-resources/chat-bot/hooks/useUpdateLiveChatAppSettings";
import { resolveLiveChatAppSettings } from "@jugl-web/domain-resources/chat-bot/utils/resolveLiveChatAppSettings";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { LiveChatAppAccountMenuTab } from "./types";
import { ReactComponent as ChatStartersActiveIcon } from "./assets/chat-starters-active.svg";
import { ReactComponent as ChatStartersIcon } from "./assets/chat-starters.svg";
import { ReactComponent as GeneralInfoActiveIcon } from "./assets/general-info-active.svg";
import { ReactComponent as GeneralInfoIcon } from "./assets/general-info.svg";
import { ReactComponent as InstallActiveIcon } from "./assets/install-active.svg";
import { ReactComponent as InstallIcon } from "./assets/install.svg";
import { ReactComponent as QuickActionsActiveIcon } from "./assets/quick-actions-active.svg";
import { ReactComponent as QuickActionsIcon } from "./assets/quick-actions.svg";
import { ReactComponent as CustomButtonActiveIcon } from "./assets/custom-button-active.svg";
import { ReactComponent as CustomButtonIcon } from "./assets/custom-button.svg";
import { ReactComponent as VoiceModeActiveIcon } from "./assets/voice-mode-active.svg";
import { ReactComponent as VoiceModeIcon } from "./assets/voice-mode.svg";
import { InstallTabContent } from "./components/InstallTabContent";
import { CustomButtonTabContent } from "./components/CustomButtonTabContent";
import { GeneralInfoTabContent } from "./components/GeneralInfoTabContent";
import { QuickActionsTabContent } from "./components/QuickActionsTabContent";
import { ChatStarterTabContent } from "./components/ChatStarterTabContent";
import { VoiceChatTabContent } from "./components/VoiceChatTabContent";

export const LiveChatAppAccountScreen: React.FC = () => {
  const [selectedMenuTab, setSelectedMenuTab] =
    useState<LiveChatAppAccountMenuTab>(LiveChatAppAccountMenuTab.install);

  const { requestNavigation } = useNavigationGuardContext();
  const { entity, entityId } = useEntitySelectedProvider();
  const { liveChatAppAccount } = useUpdateLiveChatAppSettings({ entityId });
  const isCxEntity = entity.type === "cx";

  const isVoiceModeEnabled = Boolean(
    resolveLiveChatAppSettings(liveChatAppAccount?.settings).voice_config
      .enabled
  );

  const { t } = useTranslations();

  const sideMenuSections = useMemo(() => {
    const sections = [
      [
        {
          id: LiveChatAppAccountMenuTab.install,
          label: t({
            id: "client-support-page.install",
            defaultMessage: "Install",
          }),
          startIcon:
            selectedMenuTab === LiveChatAppAccountMenuTab.install ? (
              <InstallActiveIcon />
            ) : (
              <InstallIcon />
            ),
        },
      ],
      [
        {
          id: LiveChatAppAccountMenuTab.generalInfo,
          label: t({
            id: "client-support-page.general-info",
            defaultMessage: "General Info",
          }),
          startIcon:
            selectedMenuTab === LiveChatAppAccountMenuTab.generalInfo ? (
              <GeneralInfoActiveIcon />
            ) : (
              <GeneralInfoIcon />
            ),
        },
        {
          id: LiveChatAppAccountMenuTab.chatStarter,
          label: t({
            id: "client-support-page.chat-starters",
            defaultMessage: "Chat Starters",
          }),
          startIcon:
            selectedMenuTab === LiveChatAppAccountMenuTab.chatStarter ? (
              <ChatStartersActiveIcon />
            ) : (
              <ChatStartersIcon />
            ),
        },
        {
          id: LiveChatAppAccountMenuTab.customButton,
          label: t({
            id: "client-support-page.custom-button",
            defaultMessage: "Custom Button",
          }),
          startIcon:
            selectedMenuTab === LiveChatAppAccountMenuTab.customButton ? (
              <CustomButtonActiveIcon />
            ) : (
              <CustomButtonIcon />
            ),
        },
        {
          id: LiveChatAppAccountMenuTab.voiceMode,
          isHidden: !isCxEntity,
          label: t({
            id: "client-support-page.voice-mode",
            defaultMessage: "Voice mode",
          }),
          startIcon:
            selectedMenuTab === LiveChatAppAccountMenuTab.voiceMode ? (
              <VoiceModeActiveIcon />
            ) : (
              <VoiceModeIcon />
            ),
          endIcon: (
            <span className="font-secondary text-xs text-dark-600">
              {isVoiceModeEnabled
                ? t({ id: "common.on", defaultMessage: "On" })
                : t({ id: "common.off", defaultMessage: "Off" })}
            </span>
          ),
        },
        {
          id: LiveChatAppAccountMenuTab.quickActions,
          label: t({
            id: "client-support-page.quick-actions",
            defaultMessage: "Quick Actions",
          }),
          startIcon:
            selectedMenuTab === LiveChatAppAccountMenuTab.quickActions ? (
              <QuickActionsActiveIcon />
            ) : (
              <QuickActionsIcon />
            ),
        },
      ],
    ];

    return sections
      .map((section) => section.filter((item) => !item.isHidden))
      .filter((section) => section.length > 0);
  }, [isCxEntity, isVoiceModeEnabled, selectedMenuTab, t]);

  return (
    <MultiSectionLayout
      leftSide={
        <SideMenuPanel>
          {sideMenuSections.map((section, index) => {
            const isLastSection = index === sideMenuSections.length - 1;

            return (
              <Fragment key={+index}>
                <SideMenuList>
                  {section.map((item) => (
                    <SideMenuListItem
                      key={item.id}
                      label={item.label}
                      startIcon={item.startIcon}
                      endIcon={item.endIcon}
                      isSelected={selectedMenuTab === item.id}
                      onClick={() =>
                        requestNavigation(() => setSelectedMenuTab(item.id))
                      }
                    />
                  ))}
                </SideMenuList>
                {!isLastSection && <SideMenuDivider />}
              </Fragment>
            );
          })}
        </SideMenuPanel>
      }
    >
      {selectedMenuTab === LiveChatAppAccountMenuTab.install && (
        <InstallTabContent />
      )}
      {selectedMenuTab === LiveChatAppAccountMenuTab.generalInfo && (
        <GeneralInfoTabContent />
      )}
      {selectedMenuTab === LiveChatAppAccountMenuTab.chatStarter && (
        <ChatStarterTabContent />
      )}
      {selectedMenuTab === LiveChatAppAccountMenuTab.customButton && (
        <CustomButtonTabContent />
      )}
      {isCxEntity &&
        selectedMenuTab === LiveChatAppAccountMenuTab.voiceMode && (
          <VoiceChatTabContent />
        )}
      {selectedMenuTab === LiveChatAppAccountMenuTab.quickActions && (
        <QuickActionsTabContent />
      )}
    </MultiSectionLayout>
  );
};
